import { inject, injectable } from 'inversify';
import { ReplaySubject, firstValueFrom, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import {
    flatten,
    uniq
} from '../utils/lodash-min';

import { FileUpload } from '../models/file-upload';
import { FileUploadStatusTracker, PollMetricsSink } from '../models/file-upload-status-tracker';
import { MetricEventName } from '../types/metric-events';
import { ProcessStageSubStateTypes, ProcessStageTypes, WebsocketResponse } from '../types/types';
import { JSONUtils } from '../utils/json.utils';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { ConnectivityUtils } from '../utils/connectivity.utils';
import { MetricsService } from './metrics.service';
import { ProcessCompleteCheckService } from './process-complete-check.service';
import { IFileManagerService } from './service-interfaces';
import { SettingsService } from './settings.service';
import { StorageKeyPrefixeTypes, StorageService } from './storage.service';
import { UIContext } from './ui-context';
import { WebSocketService } from './websocket.service';
import { scheduleCoordinator } from './scheduling/schedule-coordinator';

const FILE_DELETE_ENDPOINT = '/file-manager/delete/';

/**
 * Which transport delivered a progress message, and whether the pod tracker was
 * on its per-edge fallback at the time. Reported on CONVERT_COMPLETE/_ERROR:
 * after 2026-09-01 "did this conversion finish over the websocket, the pod poll,
 * or the edge fallback" is the first question asked of any completion-rate dip.
 */
export interface ProgressSource {
    mode: 'websocket' | 'poll';
    pollFallback: boolean;
}

@injectable()
export class FileManagerService implements IFileManagerService {
    public readonly instanceId = Math.random().toString(36).substring(7);
    logger = {
        error: (...args) => console.log,
        trace7: (...args) => console.log,
    };

    private fileUploadTrackers: FileUploadStatusTracker[] = [];

    private _fileUploadsListChanged: ReplaySubject<FileUpload[]> = new ReplaySubject(1, Infinity);
    get fileUploadsListChanged() {
        return this._fileUploadsListChanged.asObservable();
    }

    private _fileUploadChanged: ReplaySubject<FileUpload> = new ReplaySubject(1);
    get fileUploadChanged() {
        return this._fileUploadChanged.asObservable();
    }

    /** File upload ids that already produced a CONVERT_COMPLETE/_ERROR. */
    private readonly convertTerminalEmitted = new Set<string>();

    private fileTrackerInitialized = false;
    private useWebsockets = false;
    private useTaskScheduling = false;
    private podTracker: FileUploadStatusTracker | null = null;

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(StorageService) private storageService: StorageService,
        @inject(UIContext) private uiContext: UIContext,
        @inject(ProcessCompleteCheckService) private processCompleteCheckService: ProcessCompleteCheckService,
        @inject(WebSocketService) private wsService: WebSocketService,
        @inject(SettingsService) private settingsService: SettingsService,
        @inject(MetricsService) private metricsService: MetricsService,
    ) {
        this.settingsService.featureFlags.subscribe(ff => {
            const prevUseWebsockets = this.useWebsockets;
            this.useWebsockets = ff?.useWebsockets ?? false;
            this.useTaskScheduling = ff?.useTaskScheduling ?? false;
            console.log('[FileManager] Settings loaded: useTaskScheduling=', this.useTaskScheduling, 'useWebsockets=', this.useWebsockets);
            if (this.useTaskScheduling) {
                this.ensurePodTracker();
                // Move any files already in per-edge/untracked trackers to the pod tracker
                this.migrateFilesToPodTracker();
            }
            if (this.useWebsockets && !prevUseWebsockets) {
                this.initFileTrackingUsingWebSocket();
            }
            if (!this.useWebsockets || this.useTaskScheduling) {
                this.initFileTracker();
            }
        });
    }

    private initFileTrackingUsingWebSocket() {
        this.wsService.getWebsocketMessageObserver().subscribe({
            next: message => {
                if ((message as any).type === 'FALLBACK_TO_POLLING') {
                    this.switchToPolling();
                    return;
                }
                this.applyHistory(null, message, { mode: 'websocket', pollFallback: false });
            },
            error: err => this.uiContext.handleError(err),
        });
    }

    /**
     * Switch from websocket to HTTP polling mid-flight.
     * Triggered by FALLBACK_TO_POLLING signal from falcon when useWebsockets is toggled off.
     * Registers all in-flight file uploads with the tracker so replay-history picks them up.
     */
    private switchToPolling() {
        this.useWebsockets = false;
        const inFlightUploads = this.getFileUploads().filter(f => !(f.isCompleted() || f.isFailed()));
        this.initFileTracker();
        inFlightUploads.forEach(f => {
            if (!this.getFileUploadTracker(f)) {
                const tracker = FileUploadStatusTracker.createFromFileUpload(f);
                if (tracker) {
                    this.fileUploadTrackers.push(tracker);
                    tracker.addFileUpload(f);
                }
            }
        });
    }

    /**
     * Metrics seam handed to each tracker's poll (POD_POLL_RTT / POD_POLL_FALLBACK).
     * Bound once and defensive: a metrics failure must never break progress polling.
     */
    private readonly pollMetrics: PollMetricsSink = (event: MetricEventName, attributes) => {
        try {
            this.metricsService.emit(event, attributes);
        } catch { /* observability must never break the poll loop */ }
    };

    /**
     * 1 s progress-poll loop.
     *
     * The guard is PER TRACKER, not per tick (2026-09-02): the single shared
     * `isRequestInProgress` flag meant one host that answered slowly — on
     * 2026-09-01, a pod host whose Tomcat was dead behind a live nginx, so every
     * other poll hung for the 10-60 s upstream timeout — held every other
     * tracker's progress hostage and cards never reached "Completed". A tracker
     * whose previous poll is still in flight now simply sits this tick out
     * (`isPollInFlight`, enforced inside the tracker too) while the others poll,
     * which keeps the one-request-per-tracker-at-a-time rule and so does not add
     * any load to the pod. allSettled (not all) so one rejection cannot take the
     * tick — or the subscription — down with it.
     */
    private initFileTracker() {
        if (!this.fileTrackerInitialized) {
            this.fileTrackerInitialized = true;
            interval(1000).subscribe(
                async () => {
                    const polls: Promise<any>[] = [];
                    for (const tracker of this.fileUploadTrackers) {
                        if (tracker.isPollInFlight) {
                            continue;
                        }
                        polls.push(tracker.replayHistory(
                            this.http,
                            (fileUpload: FileUpload, websocketResponse: WebsocketResponse) => {
                                this.applyHistory(fileUpload, websocketResponse, {
                                    mode: 'poll',
                                    pollFallback: tracker.isUsingEdgeFallback,
                                })
                            },
                            this.pollMetrics,
                        ));
                    }
                    await Promise.allSettled(polls);
                }
            );
        }
    }

    public async removeFile(fileUpload: FileUpload) {
        let removed = false;
        for (const tracker of this.fileUploadTrackers) {
            if (tracker.hasFileUpload(fileUpload.id)) {
                tracker.removeFileUpload(fileUpload.id);
                removed = true;
            }
        }
        if (!removed) {
            console.error('File does not exist on any Tracker');
        }
        // A removed card is no longer the scheduler's business: drop its queued
        // state, its nonce and its watchdog timer. `removeFile` is now reached
        // ONLY on user cancel/remove or a terminal 4xx (plan §4.1) — a transient
        // schedule failure must never get here, because this method also deletes
        // the uploaded bytes on all three edge domains (correction 16).
        scheduleCoordinator.forget(fileUpload.id);
        this.notifyFileUploadsListChanged();
        this.removeStoredFileUpload(fileUpload);
        this.removeRemoteFileAsync(fileUpload);
    }

    private removeStoredFileUpload(fileUpload: FileUpload) {
        this.storageService.remove(StorageKeyPrefixeTypes.FILE_UPLOAD_KEY_PREFIX, fileUpload.id);
    }

    private removeRemoteFileAsync(fileUpload: FileUpload): void {
        (async () => {
            try {
                try {
                    const urls = this.getFileDeleteUrls(fileUpload);
                    for (const url of urls) {
                        try {
                            await firstValueFrom(this.http.get(url));
                        } catch (e) {
                            this.logger.error({
                                message: 'Error while removing file from remote server',
                                error: e,
                                data: FileUpload.toLoggableObject(fileUpload),
                            });
                        }
                    }
                } catch (e) {
                    this.logger.error({
                        message: 'Error while getting URL for removing file from storage',
                        error: e,
                        data: FileUpload.toLoggableObject(fileUpload),
                    });
                }

            } catch (e) {
                this.logger.error({
                    message: 'Error while removing file from local storage',
                    error: e,
                    data: FileUpload.toLoggableObject(fileUpload),
                });
            }
        })();
    }

    private removeFileUploadFromTracker(tracker: FileUploadStatusTracker, fileUploadId: string) {
        tracker.removeFileUpload(fileUploadId);
        this.notifyFileUploadsListChanged();
    }

    private notifyFileUploadsListChanged() {
        const fileUploads = this.getFileUploads();
        this._fileUploadsListChanged.next(fileUploads);
    }

    private notifyFileUploadChanged(fileUpload: FileUpload) {
        this._fileUploadChanged.next(fileUpload);
    }

    public async removeAllFiles(fileUploads: FileUpload[]) {
        for (const fileUpload of fileUploads) {
            await this.removeFile(fileUpload);
        }
    }

    private getFileDeleteUrls(fileUpload: FileUpload): string[] {
        const metadata = fileUpload.endpointData.processorMetadataList[0];
        const domains = [
            metadata.downloadDomain,
            metadata.uploadDomain,
            metadata.processDomain,
        ];
        return uniq(domains).map(domain => {
            return ApiConfig.suffixDomainWithHttpsEdgeEndpoint(domain) + FILE_DELETE_ENDPOINT + fileUpload.id;
        });
    }

    public getFileUploads(): FileUpload[] {
        return flatten(this.fileUploadTrackers.map(t => t.getFileUploads())).filter(t => t);
    }

    private getFileUploadsById(fileUploadId: string): FileUpload[] {
        return this.getFileUploads().filter(f => f.id === fileUploadId);
    }

    private migrateFilesToPodTracker() {
        if (!this.podTracker) return;
        for (const tracker of this.fileUploadTrackers) {
            if (tracker === this.podTracker) continue;
            const files = tracker.getFileUploads();
            for (const file of files) {
                if (!this.podTracker.hasFileUpload(file.id)) {
                    this.podTracker.addFileUpload(file);
                }
                tracker.removeFileUpload(file.id);
            }
        }
    }

    private ensurePodTracker(): boolean {
        if (this.podTracker) { console.log('[FileManager] podTracker exists'); return true; }
        // Try sync read from ReplaySubject if settings haven't been consumed yet
        if (!this.useTaskScheduling) {
            this.settingsService.featureFlags.pipe(take(1)).subscribe(ff => {
                this.useTaskScheduling = ff?.useTaskScheduling ?? false;
                console.log('[FileManager] ensurePodTracker sync read: useTaskScheduling=', this.useTaskScheduling);
            });
        }
        if (!this.useTaskScheduling) { console.log('[FileManager] useTaskScheduling=false, skipping pod'); return false; }
        console.log('[FileManager] Creating pod tracker for', this.apiConfig.podEndpoint);
        this.podTracker = FileUploadStatusTracker.createForPod(this.apiConfig.podEndpoint);
        this.fileUploadTrackers.push(this.podTracker);
        return true;
    }

    /** Add FileUploads. We are not tracking UploadInProgress files */
    public addFileUpload(fileUpload: FileUpload) {
        this.addFileUploads([fileUpload]);
    }

    public addFileUploads(fileUploads: FileUpload[]) {
        // Only start HTTP polling if WebSocket is off. When useWebsockets=true,
        // the constructor already called initFileTrackingUsingWebSocket().
        // Without this guard, both paths run simultaneously (C1 bug).
        if (!this.useWebsockets) {
            this.initFileTracker();
        }
        fileUploads.forEach((fileUpload, index) => {
            if (this.ensurePodTracker()) {
                console.log('[FileManager] Adding file to pod tracker:', fileUpload.id);
                this.podTracker.addFileUpload(fileUpload);
            } else {
                console.log('[FileManager] Adding file to edge tracker:', fileUpload.id);
                let tracker = this.getFileUploadTracker(fileUpload);
                if (!tracker) {
                    tracker = FileUploadStatusTracker.createFromFileUpload(fileUpload);
                    if (tracker) {
                        this.fileUploadTrackers.push(tracker);
                    }
                }
                if (tracker) {
                    tracker.addFileUpload(fileUpload);
                }
            }
        });
        this.notifyFileUploadsListChanged();
    }

    private getFileUploadTracker(fileUpload: FileUpload) {
        return this.fileUploadTrackers.find(t => t.sameAs(fileUpload));
    }

    /** Re-track a file upload after its process domain changes (e.g., on retry) */
    public retrackFileUpload(fileUpload: FileUpload) {
        // Remove from ALL trackers that contain this file ID — the file may have
        // been added to an untracked tracker (null endpoint) before processDomain
        // was set, so matching by endpoint alone misses it.
        for (const tracker of this.fileUploadTrackers) {
            if (tracker.hasFileUpload(fileUpload.id)) {
                tracker.removeFileUpload(fileUpload.id);
            }
        }
        this.addFileUpload(fileUpload);
    }

    saveFileUploadToStorage(fileUpload: FileUpload) {
        this.storageService.store(StorageKeyPrefixeTypes.FILE_UPLOAD_KEY_PREFIX, fileUpload.id, fileUpload);
        const untractedTracker = this.fileUploadTrackers.find(t => !t.canTrack);
        if (untractedTracker) {
            const fileUploads = untractedTracker.getFileUploads();
            (fileUploads || []).forEach(fileUpload => {
                let canCreate = FileUploadStatusTracker.canCreateTrackedFileUploadTracker(fileUpload);
                if (canCreate) {
                    this.removeFileUploadFromTracker(untractedTracker, fileUpload.id);
                    this.addFileUpload(fileUpload);
                }
            });
        }
    }

    private saveFileUploadToStorageWithoutRecomputingUntrackedFileUploads(fileUpload: FileUpload) {
        this.storageService.store(StorageKeyPrefixeTypes.FILE_UPLOAD_KEY_PREFIX, fileUpload.id, fileUpload);
    }

    checkStatus(fileInfo: FileUpload) {
        if (!ConnectivityUtils.isOnline()) return Promise.resolve();
        return this.processCompleteCheckService.requestToSendFileCompleteDataThroughWS(
            fileInfo.endpointData.processorMetadataList[0].processDomain,
            fileInfo.id,
        )
            .then(data => this.logger.trace7({ message: `Check fileUpload status.`, data }))
            .catch(err => this.logger.error({ message: 'Error checking fileUpload status.', error: err }));
    }

    /**
     *
     * @param _fileUpload to apply ws reply to a fileUpload on this session, fileUpload should be null.
     * There is a recovered version of same fileUpload on recoveredFileUploads, sending to that wouldnt update the UI
     * @param websocketResponse
     */
    public applyHistory(
        _fileUpload: FileUpload,
        websocketResponse: WebsocketResponse,
        source: ProgressSource = { mode: 'websocket', pollFallback: false },
    ) {
        const fileUploads = _fileUpload && [_fileUpload] || this.getFileUploadsById(websocketResponse.uuid);
        if (fileUploads && fileUploads.length > 0) {
            fileUploads.forEach(fileUpload => {
                // Monotonic eventId guard — prevents progress bar regression.
                // eventIds are now stored on task documents from the edge's counter,
                // monotonic for both WebSocket and replay-history paths.
                const isTerminal = websocketResponse.processStage === ProcessStageTypes.COMPLETED;
                if (!isTerminal && websocketResponse.eventId && (fileUpload as any)._lastEventId
                    && websocketResponse.eventId <= (fileUpload as any)._lastEventId) {
                    return;
                }
                if (websocketResponse.eventId) {
                    (fileUpload as any)._lastEventId = websocketResponse.eventId;
                }

                // only update result with ws message it we are allowed to
                if (!fileUpload.discartWSReply) {
                    const isTerminalMessage = websocketResponse.processStage === ProcessStageTypes.COMPLETED;

                    // Once completed/failed, ignore all non-terminal messages
                    if (fileUpload.result && !fileUpload.result.error && !isTerminalMessage) {
                        return;
                    }

                    fileUpload.ws = websocketResponse;
                    if (!fileUpload.result || fileUpload.result.error || isTerminalMessage) {
                        fileUpload.result = websocketResponse.result && JSON.parse(websocketResponse.result);
                        // Patch edge domains from ProcessResult (set by ironman on completion)
                        if (fileUpload.result?.processDomain && fileUpload.endpointData?.processorMetadataList?.[0]) {
                            fileUpload.endpointData.processorMetadataList[0].processDomain = fileUpload.result.processDomain;
                        }
                        if (fileUpload.result?.downloadDomain && fileUpload.endpointData?.processorMetadataList?.[0]) {
                            fileUpload.endpointData.processorMetadataList[0].downloadDomain = fileUpload.result.downloadDomain;
                        }
                        this.saveFileUploadToStorageWithoutRecomputingUntrackedFileUploads(fileUpload);
                    } else {
                        if (!fileUpload.processEndTime) {
                            fileUpload.processEndTime = new Date();
                        }
                    }
                }

                if (fileUpload && fileUpload.result) {
                    const result = fileUpload.result;
                    if (websocketResponse.processStage === ProcessStageTypes.COMPLETED && websocketResponse.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
                        fileUpload.processEndTime = result.end;
                        fileUpload.processStartTime = result.start || fileUpload.processStartTime;
                        this.saveFileUploadToStorage(fileUpload);
                    }

                    if (websocketResponse.processStage === ProcessStageTypes.ZIP &&
                        (!fileUpload.zipResult || (fileUpload.zipResult && fileUpload.zipResult.processStageSubState !== ProcessStageSubStateTypes.COMPLETED))
                    ) {
                        fileUpload.zipResult = websocketResponse;
                        if (websocketResponse.processStageSubState === ProcessStageSubStateTypes.COMPLETED) {
                            this.saveFileUploadToStorageWithoutRecomputingUntrackedFileUploads(fileUpload);
                        }
                    }
                }
                // Feed the hands-off scheduling watchdog (clientQueuedState,
                // MONGO-STALL-PLAN-v5.2.md §4.3, correction 22). This is the only
                // place the client learns that work has actually STARTED, and
                // without it nothing ever concludes "scheduled but never started"
                // — the pod poll simply spins forever on a non-terminal card.
                // A no-op for any card the coordinator is not tracking, so the
                // flag-off path is unchanged.
                if (scheduleCoordinator.has(fileUpload.id)) {
                    if (websocketResponse.processStage === ProcessStageTypes.COMPLETED) {
                        scheduleCoordinator.onCompleted(fileUpload.id);
                    } else {
                        scheduleCoordinator.onProgress(fileUpload.id);
                    }
                }
                this.emitConvertTerminal(fileUpload, websocketResponse, source);
                this.notifyFileUploadChanged(fileUpload);
            });
        }
    }

    /**
     * CONVERT_COMPLETE / CONVERT_ERROR — the far end of the funnel.
     *
     * This is the decision point: the terminal `processStage` for one workflow,
     * whichever transport delivered it. Until now the funnel stopped at
     * CONVERT_START (4.7k/day) and resumed at FILE_DOWNLOAD, so the 29 % of
     * sessions that never downloaded could equally have been failed
     * conversions, lost progress messages or people who simply left, and there
     * was nothing to tell them apart.
     *
     * Dedupe: exactly ONE terminal event per file upload (decision 2026-09-02).
     * The same terminal message legitimately arrives more than once — the
     * websocket push and the 1 s replay-history poll both deliver it, retries
     * re-apply it, and `applyHistory` deliberately does NOT ignore repeat
     * terminal messages (it re-parses them to patch edge domains). Counting
     * each delivery would inflate CONVERT_COMPLETE against CONVERT_START and
     * make the funnel's last step read above 100 %.
     */
    private emitConvertTerminal(
        fileUpload: FileUpload,
        websocketResponse: WebsocketResponse,
        source: ProgressSource,
    ) {
        try {
            // A stage-level FAILED (e.g. CONVERT/FAILED) is the FIRST failure signal in poll mode
            // and the tracker drops the file on it, so it is the one we must emit on. The edge
            // attaches the persisted ProcessResult (workflowId, failureCode) to that history entry
            // since 2026-09-02 (replay-history); an older edge yields failureCode=UNKNOWN.
            if (websocketResponse.processStage !== ProcessStageTypes.COMPLETED
                && websocketResponse.processStageSubState !== ProcessStageSubStateTypes.FAILED) {
                return;
            }
            if (!fileUpload?.id || this.convertTerminalEmitted.has(fileUpload.id)) {
                return;
            }
            const result = fileUpload.result;
            const failed = websocketResponse.processStageSubState === ProcessStageSubStateTypes.FAILED
                || result?.error === true;
            if (!failed && websocketResponse.processStageSubState !== ProcessStageSubStateTypes.COMPLETED) {
                return; // COMPLETED stage but still partial/started — not terminal yet.
            }
            this.convertTerminalEmitted.add(fileUpload.id);

            const ms = this.metricsService.takeConvertStartMs(fileUpload.id);
            const attributes: Record<string, string | number | boolean> = {
                page: fileUpload.pageData?.path || fileUpload.uniqueProcessType || '',
                // The workflow's Mongo ObjectId when the edge sent one (the join
                // key to `workflows` and to Graylog's requestId lines); the
                // client-side upload id otherwise.
                workflowId: String(result?.workflowId || fileUpload.id),
                mode: source.mode,
                pollFallback: source.pollFallback,
            };
            if (ms != null) {
                attributes.ms = ms;
            }
            if (failed) {
                // `failureCode` is a new OPTIONAL backend field (METRICS.md §5).
                // An edge on an older jar simply omits it, and UNKNOWN is the
                // documented value for that — never drop the event over it.
                attributes.failureCode = String(result?.failureCode || 'UNKNOWN');
            }
            this.metricsService.emit(
                failed ? MetricEventName.CONVERT_ERROR : MetricEventName.CONVERT_COMPLETE,
                attributes,
            );
        } catch { /* observability must never break progress handling */ }
    }

    public stringifyAllUploadedFileUploads() {
        try {
            let fileArray = [];
            (this.getFileUploads() || []).forEach(fileUpload => {
                try {
                    fileArray.push(FileUpload.toLoggableObject(fileUpload));
                } catch (e) {
                    this.logger.error({
                        message: 'Failed to serialize a file upload',
                        error: e,
                    });
                }
            });
            return JSONUtils.safeStringify(fileArray);
        } catch (e) {
            this.logger.error({
                message: 'Failed to serialize file uploads',
                error: e,
            });
        }
    }
}
